<template>
  <div class="app" :class="[pageClassName, {'is-fullscreen-mode': isFullScreen, 'full-container': isStretchFull, 'right-sidebar-open': $store.state.RightSidebar.isOpen}]">
    <b-loading :model-value="isLoading || authLoading"></b-loading>
    <network>
      <template #offline>
        <offline-network-overlay></offline-network-overlay>
      </template>
    </network>
    <div class="embed-error" v-if="embedError">
      <p>Unable to load video. Please refresh the page or contact your administrator.</p>
    </div>
    <template v-else-if="!authLoading || $route.meta.unsecured">
      <component :is="layout" v-if="layout">
        <router-view :key="routeKey"/>
      </component>
      <router-view v-else :key="routeKey"/>
    </template>
    <patient-overlay-video v-if="canAccessVideoMeetings"></patient-overlay-video>
    <patient-quick-edit-modal ref="patientQuickEditModal"></patient-quick-edit-modal>
    <div class="right-sidebar" v-if="$store.state.RightSidebar.isOpen">
      <component
        :is="$store.state.RightSidebar.component"
        :key="$store.state.RightSidebar.reloadKey"
        v-bind="$store.state.RightSidebar.props"
        @close="closeSidebar"
      ></component>
    </div>
  </div>
</template>

<script>
  import Network from "@/components/Network";
  import BlankLayout from "@/layouts/BlankLayout";
  import PublicLayout from "@/layouts/PublicLayout.vue";
  import { mapState } from "vuex";
  import PatientOverlayVideo from "@/components/PatientOverlayVideo";
  import OfflineNetworkOverlay from "@/components/OfflineNetworkOverlay";
  import PatientQuickEditModal from "@/components/PatientQuickEditModal";
  import QuickView from "@/components/QuickView.vue";
  import VideoVisitSidebar from "@/components/VideoVisitSidebar.vue";
  import to from "@/lib/to";
  import {LOG_TYPES} from "@/lib/Log";
  import {CHANNEL, CLIENT_EVENT, SERVER_EVENT} from "@/lib/Pusher";
  import {applyPermissions} from "@/lib/Permissions";
  import {EventBus, EVENTS} from "@/lib/EventBus";
  import RoomNotificationMixin from "@/components/mixins/RoomNotificationMixin";
  import * as Sentry from "@sentry/vue";
  import LogRocket from 'logrocket';

  export default {
    components: {
      OfflineNetworkOverlay,
      Network,
      PatientOverlayVideo,
      BlankLayout,
      PublicLayout,
      PatientQuickEditModal,
      QuickView,
      VideoVisitSidebar
    },
    mixins: [RoomNotificationMixin],
    mounted() {
      EventBus.$on(EVENTS.OPEN_PATIENT_QUICK_EDIT_MODAL, (patientId) => {
        if (this.$refs.patientQuickEditModal) {
          this.$refs.patientQuickEditModal.openModal(patientId)
        }
      });

      const debouncedTimerReset = this.$helpers.debounce(() => {
        this.startInactivityTimer()
      }, 10_000)

      const unwatch = this.$watch(() => {
        return [this.$store.state.token, this.$store.state.currentUser]
      }, (newVal) => {
        if (newVal.every(x => !!x)) {
          this.checkSupportedBrowser()

          this.userChannel = this.$pusher.subscribe(CHANNEL.FOR_USER({
            env: import.meta.env.VITE_ENVIRONMENT_NAME,
            user: this.$store.state.currentUser
          }));
          this.hospitalChannel = this.$pusher.subscribe(CHANNEL.FOR_HOSPITAL({
            env: import.meta.env.VITE_ENVIRONMENT_NAME,
            hospital: this.$store.state.currentHospital
          }))

          this.initializeRoomNotifications(this.hospitalChannel)

          this.userChannel.bind(SERVER_EVENT.MY_HOME_TRANSFER, this.handleMyHomeTransfer)
          this.userChannel.bind(SERVER_EVENT.TRANSFER_NEEDS_ACKNOWLEDGMENT, this.handleTransferNeedsAcknowledgment)
          this.userChannel.bind(CLIENT_EVENT.SESSION_EXPIRED, this.handleSessionExpired)
          this.userChannel.bind(SERVER_EVENT.USER_UPDATED, this.handleUserUpdated)
          this.userChannel.bind(CLIENT_EVENT.USER_AUTHENTICATED, this.handleUserAuthenticated)
          this.hospitalChannel.bind(SERVER_EVENT.REQUEST_BROWSER_REFRESH, this.handleRequestBrowserRefresh)

          // Embedded (iframe) sessions have no auth0Client to refresh against
          // and no place for support chrome — skip the inactivity plumbing
          // and HelpScout; Pusher stays (live video relies on it).
          if (this.embedMode) {
            unwatch()
            return
          }

          this.$http.interceptors.response.use(response => {
            if (response?.config?.isPusherMessage || response?.config?.skipInactivityCheck) {
              return response
            }
            if (!this.inactivityDialog) {
              debouncedTimerReset()
            }
            return response
          });

          this.$helpScout.inject({
            onError: () => {
              const snack = this.$buefy.snackbar.open({
                position: 'is-top',
                actionText: 'Dismiss',
                indefinite: true,
                message: `We would like to provide the LookDeep Support Center to you, however it appears that you have an ad blocker preventing access to the site. <br>  Please disable your ad blocker for  ${import.meta.env.VITE_BASE_URL}. <br> Note that we will never run any ads on this site.`,
                onAction() {
                  snack.close()
                }
              })
            }
          })

          unwatch()
        }
      }, { immediate: true })

      EventBus.$on(EVENTS.HELPSCOUT_VISIBILITY, (visibility) => {
        document.body.classList.toggle('helpscout-hidden', !visibility)
      })
    },

    methods: {
      handleMyHomeTransfer(payload) {
        this.$buefy.toast.open({
          message: `The ${this.$helpers.escapeHtml(payload.staff_type)} Assignment has been transferred to you`,
          type: 'is-success',
          duration: 4000
        })
      },
      handleTransferNeedsAcknowledgment() {
        if (this.$route.name != 'my-assignments') {
          this.$buefy.snackbar.open({
            message: 'You Have New Patients Assigned To You',
            type: 'is-warning room-notification',
            position: 'is-top',
            indefinite: true,
            actionText: 'View Patients',
            onAction: () => {
              this.$router.push({ name: 'my-assignments' })
            }
          })
        }
      },
      handleSessionExpired() {
        this.showSessionExpiredPopup()
      },
      handleUserUpdated(payload) {
        this.userUpdated(payload)
      },
      handleUserAuthenticated(payload) {
        if (payload.uniqueId != this.$store.state.uniqueSessionId) {
          this.$buefy.snackbar.open({
            type: 'is-danger',
            position: 'is-top',
            cancelText: null,
            indefinite: true,
            actionText: 'Dismiss',
            message: `Your account has been logged into from another device.<br/>If this was not you, please contact support immediately.`,
          })
        }
      },
      handleRequestBrowserRefresh({ message }) {
        this.$buefy.snackbar.open({
          type: 'is-success',
          position: 'is-top',
          cancelText: null,
          indefinite: true,
          actionText: 'Refresh',
          message: message || 'Please refresh your browser',
          onAction: () => {
            this.$store.commit('toggleLoading', { loadingState: true })
            window.location.reload()
          }
        })
      },
      async checkSupportedBrowser() {
        if (!(/chrome/i.test(navigator.userAgent))) {
          this.$log.warn('unsupported browser detected', { user_agent: navigator.userAgent })
          this.$buefy.snackbar.open({
            type: 'is-warning room-notification',
            position: 'is-top',
            cancelText: 'Dismiss',
            indefinite: true,
            actionText: null,
            message: 'Your browser is unsupported. Please switch to Chrome for full functionality.'
          })
        }
      },
      logOutUser() {
        this.$logger.logUser(LOG_TYPES.USER.USER_SESSION_EXPIRED)
        this.$auth?.logOut()
      },
      showSessionExpiredPopup() {
        this.$buefy.dialog.alert({
          message: 'Your session expired',
          confirmText: 'Sign-In Again',
          onConfirm: () => {
            this.$store.commit('toggleLoading', { loadingState: true })
            this.logOutUser()
            // this.userChannel.trigger(CLIENT_EVENT.LOGOUT_WITH_EXPIRATION, '')
            this.triggerUser(CLIENT_EVENT.LOGOUT_WITH_EXPIRATION, '')
          }
        })
      },
      showReSignInPopup(message) {
        this.$buefy.dialog.alert({
          title: 'Sign-In Required',
          message: message || 'Your account has not been provisioned in LookDeep. Please contact a manager.',
          confirmText: 'Sign-In Again',
          closeOnConfirm: false,
          onConfirm: () => {
            this.$store.commit('toggleLoading', { loadingState: true })
            // Clear the cached org so sign-in starts fresh, then log out of Auth0 (full redirect),
            // which re-initiates the sign-in flow.
            localStorage.removeItem('auth0_org_id')
            this.$auth?.logOut()
          }
        })
      },
      startInactivityTimer() {
        if (this.embedMode) return

        clearTimeout(this.inactivityTimer);
        clearTimeout(this.stillThereModalTimer)

        let INACTIVITY_TIMEOUT = 55 * 60 * 1000;
        let INACTIVITY_MESSAGE_TIMEOUT = 3 * 60 * 1000; // 3 minutes for popup duration
        const serverInactivityTimeout = this.$store.state.currentUser.inactivity_timeout_sec
        if (serverInactivityTimeout) {
          /**
           * Show 'Still There?' popup 5 minutes before session expires
           * Popup runs for 3 minutes, then auto logout happens 2 minutes before actual expiration
           */
          const fiveMinutes = 5 * 60 * 1000
          INACTIVITY_TIMEOUT = (serverInactivityTimeout * 1000) - fiveMinutes
        }

        if (this.inactivityDialog) {
          this.inactivityDialog.close()
        }
        this.inactivityTimer = setTimeout(() => {
          this.inactivityDialog = this.$buefy.dialog.alert({
            title: 'Still There ?',
            message: 'Please click the button below to stay logged in.',
            confirmText: 'Stay Logged In',
            closeOnConfirm: false,
            onConfirm: async (value, { close }) => {
              const token = await this.$auth.forceGetTokenSilently()
              this.$auth.setToken(token)

              const [err,] = await to(this.$http.get('/users/current'))
              if (err) {
                this.logOutUser()
                this.triggerUser(CLIENT_EVENT.LOGOUT_WITH_EXPIRATION, '')
              } else {
                close()
                this.inactivityDialog = null
              }
            }
          });
          this.stillThereModalTimer = setTimeout(() => {
            this.logOutUser()
            this.triggerUser(CLIENT_EVENT.LOGOUT_WITH_EXPIRATION, '')
          }, INACTIVITY_MESSAGE_TIMEOUT);
        }, INACTIVITY_TIMEOUT);
      },
      async userUpdated() {
        const [err, response] = await to(this.fetchCurrentUser())
        if (!err) {
          const { data } = response
          this.$store.commit('setCurrentUser', data)
          applyPermissions(data.permissions)
        }
      },
      async triggerUser(event, data) {
        return to(this.$http.post('/pusher/trigger_user', { event, data }, { skipDivisions: true, skipLoading: true, isPusherMessage: true }))
      },
      async fetchCurrentUser() {
        return this.$http.get('/users/current', { skipDivisions: true })
      },
      closeSidebar() {
        this.$store.dispatch('RightSidebar/close')
        EventBus.$emit(EVENTS.QUICKVIEW_VISIBILITY_CHANGE, false)
        this.$store.commit('UIStore/setSidebarReduced', this.previousSidebarReducedState)
      },
      helpScoutOnReady() {
        const identifyParams = {
          name: this.$store.state.currentUser.name,
          email: this.$store.state.currentUser.email
        }

        if (this.$store.state.currentUser.helpscout_signature) {
          identifyParams.signature = this.$store.state.currentUser.helpscout_signature
        }

        LogRocket.getSessionURL(sessionURL => {
          identifyParams.LogrocketSessionURL = sessionURL
          this.$helpScout.Beacon('identify', identifyParams)
        });

        this.$helpScout.Beacon('on', 'open', () => {
          this.$helpScout.Beacon('event', {
            type: 'page-viewed',
            url: document.location.href,
            title: document.title,
          })
          this.$helpScout.Beacon('suggest')
          this.$helpScout.Beacon('session-data', {
            'LogRocket Session ID': LogRocket.recordingID,
            'LogRocket Session URL': identifyParams.LogrocketSessionURL
          })
        })
        this.$helpScout.Beacon('on', 'email-sent', ({ name, email, text }) => {
          const message = `${text}\nLogrocket Session:${LogRocket.recordingID}`
          Sentry.captureFeedback({ name, email, message }, { includeReplay: true });
        })
      }
    },
    created() {
      document.addEventListener('fullscreenchange', () => {
        this.isFullScreen = document.fullscreenElement != null
      });

      this.$watch(() => {
        return this.$store.state.currentUser
      }, () => {
        if (this.$store.state.currentUser && this.$store.state.currentUser.name) {
          this.$helpScout.onReady(this.helpScoutOnReady)
          LogRocket.identify(this.$store.state.currentUser.id + '', {
            name: this.$store.state.currentUser.name,
            email: this.$store.state.currentUser.email,
            hospital_id: this.$store.state.currentUser.hospital_id
          })
          LogRocket.getSessionURL(sessionURL => {
            Sentry.getCurrentScope().setExtras({
              'LogrocketSessionURL': sessionURL
            });
            this.$store.commit('setLogrocketSessionId', LogRocket.recordingID)
          });
        }
      }, { immediate: true })

      this.$store.subscribe(mutation => {
        if (mutation.type === 'setNetworkErrors') {
          const code = this.networkErrors[0]?.code
          const errorData = this.networkErrors[0]?.response?.data
          const errorType = errorData?.type

          // SSO user authenticated with the IdP but has no provisioned LookDeep account
          if (errorType === 'UserAuthenticator::UnprovisionedSSOUser') {
            this.dialogs.forEach(d => d.close())
            this.showReSignInPopup(errorData?.error)
            return
          }

          if (this.networkErrors.length === 1 && (code === 'ECONNABORTED' || errorType === 'UserAuthenticator::InactivityTimeout')) {
            this.dialogs.forEach(d => d.close())
            if (code === 'ECONNABORTED') {
              this.$log.warn('inactivity timeout', { network_errors: this.networkErrors })
              // let message = 'An Error Occurred - Please refresh your browser'
              // this.dialogs.push(this.$buefy.dialog.alert({
              //   title: 'Timeout', message
              // }))
            } else if (errorType === 'UserAuthenticator::InactivityTimeout') {
              // this.userChannel.trigger(CLIENT_EVENT.SESSION_EXPIRED, '')
              this.triggerUser(CLIENT_EVENT.SESSION_EXPIRED, '')
              this.showSessionExpiredPopup()
            }
          } else {
            if (this.networkErrors.length > 0) {
              const dialog = this.$buefy.dialog.alert({
                title: 'Something Went Wrong',
                message: this.networkErrors.reduce((acc, curr) => {
                  return `${this.$helpers.escapeHtml(curr.response?.data?.error) || 'Unknown network error. Please reload the page.'}`
                }, ''),
                onConfirm: () => {
                  this.dialogs.pop()
                }
              });
              this.dialogs.push(dialog)
            }
          }
        }
      });
    },
    computed: {
      ...mapState(['divisionId']),
      layout() {
        return this.$route.meta.layout ? this.$route.meta.layout + "-layout" : null;
      },
      ...mapState(['isLoading', 'networkErrors', 'token', 'authLoading']),
      canAccessVideoMeetings() {
        return this.$can('AccessLiveVideo', 'Meeting')
      },
      embedMode() {
        return this.$store.state.UIStore.embedMode
      },
      embedError() {
        return this.$store.state.UIStore.embedError
      },
      pageClassName() {
        return this.$route.name ? 'page-' + this.$route.name.replaceAll('.', '-') : ''
      },
      routeKey() {
        const { path, query: { menu_id, item_id} } = this.$route
        return '' + path + menu_id + item_id
      },
      isStretchFull() {
        return !!this.$store.getters['UserPreferencesStore/getPreference']('stretch_pages')?.enabled
      },
      themePreference() {
        const pref = this.$store.getters['UserPreferencesStore/getPreference']('theme')
        return pref?.mode ?? 'light'
      }
    },
    data() {
      return {
        inactivityTimer: null,
        stillThereModalTimer: null,
        isFullScreen: false,
        dialogs: [],
        userChannel: null,
        hospitalChannel: null,
        inactivityDialog: null,
        previousSidebarReducedState: false
      }
    },
    watch: {
      themePreference: {
        handler(val) {
          document.documentElement.setAttribute('data-theme', val)
          localStorage.setItem('ld_theme', val)
        },
        immediate: true
      },
      '$store.state.RightSidebar.isOpen': {
        handler(newVal) {
          if (newVal) {
            this.previousSidebarReducedState = this.$store.state.UIStore.sidebarReduced
            this.$store.commit('UIStore/setSidebarReduced', true)
          } else {
            this.$store.commit('UIStore/setSidebarReduced', this.previousSidebarReducedState)
          }
          EventBus.$emit(EVENTS.QUICKVIEW_VISIBILITY_CHANGE, newVal)
        },
        immediate: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  .loading-overlay {
    z-index: 999999999 !important;
  }
  .right-sidebar {
    position: fixed;
    top: 60px;
    right: 20px;
    width: var(--quickview-width);
    border-radius: 8px 8px 0 0;
    height: calc(100vh - 60px);
    transition: width 150ms ease-out;
    overflow: hidden;
    z-index: 100000;
    :deep(.quickview) {
      width: var(--quickview-width);
    }
  }
  .app.right-sidebar-open .right-sidebar {
    width: var(--quickview-width);
  }
  .embed-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    padding: 20px;
    text-align: center;
    color: #4a4a4a;
  }
</style>